import type { World } from '../sim/world';
import type { SimEvent } from '../sim/events';
import { BELFRY, deckY } from '../data/ship';
import { Vec3 } from '../core/math';

/** Vowel formants (Hz) for the crew's voices. */
const VOWELS: Record<string, [number, number]> = { a: [760, 1200], o: [500, 880], e: [480, 1850], u: [350, 800], ae: [660, 1700] };

// Procedural, spatialised sound. Nothing is sampled: every sound is built from
// filtered noise and oscillators and driven by the simulation (speed, wind,
// sea state, rain, sail flutter, events).

interface V3 { x: number; y: number; z: number }

interface Loop {
  src: AudioBufferSourceNode;
  gain: GainNode;
  filter: BiquadFilterNode;
  panner?: PannerNode;
}

const SPEED_OF_SOUND = 343;

/**
 * One-off sounds allowed at once. Every one costs the audio thread time
 * (filters, panning); past what it can render in real time the browser
 * drops the sound out. Past the budget only what matters most is made:
 * see `admit`.
 */
const MAX_VOICES = 64;
/** How much a one-off sound matters: ambience bits, ordinary sounds, the big ones. */
type Prio = 0 | 1 | 2;
/** Splashes a frame (a case of grape lands seventy at once: a handful says it). */
const SPLASHES_PER_FRAME = 6;

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private sfx!: GainNode;
  /** Taps the output to notice it going silent from a non-finite sample (see `health`). */
  private watch!: AnalyserNode;
  private readonly watchBuf = new Float32Array(256);
  private healthTimer = 0;
  /** One-off sounds sounding now (see MAX_VOICES), and splashes made this frame. */
  private voices = 0;
  private splashes = 0;
  /** Where the listener is, for leaving out sounds too far off to hear. */
  private readonly ear: V3 = { x: 0, y: 0, z: 0 };
  /** The listener's last orientation, to tell a camera cut from a turn. */
  private readonly lastFwd = new Vec3(0, 0, -1);
  private reverbSend!: GainNode;
  private white!: AudioBuffer;
  private pink!: AudioBuffer;
  private brown!: AudioBuffer;
  private loops: Record<string, Loop> = {};
  private whistles: { f: BiquadFilterNode; g: GainNode; base: number; k: number }[] = [];
  private flapPhase = 0;
  private gullTimer = 4;
  private time = 0;
  volume = 0.8;
  muted = false;

  get started(): boolean {
    return this.ctx !== null;
  }

  /** Must be called from a user gesture. */
  start(): void {
    if (this.ctx) {
      void this.ctx.resume();
      return;
    }
    const ctx = new AudioContext({ latencyHint: 'interactive' });
    this.ctx = ctx;
    this.voices = 0;
    // A browser can suspend the context (another app takes the device, the
    // tab slept): pick it up again at the next touch of a key or the mouse.
    if (!this.waking) {
      this.waking = () => {
        if (this.ctx && this.ctx.state !== 'running' && this.ctx.state !== 'closed') void this.ctx.resume();
      };
      for (const e of ['pointerdown', 'keydown'] as const) window.addEventListener(e, this.waking, { capture: true });
      document.addEventListener('visibilitychange', this.waking);
    }
    // The mix: glue compression, the volume, then a soft ceiling that
    // leaves ordinary levels alone and never lets a pile-up past full scale.
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -16;
    comp.knee.value = 12;
    comp.ratio.value = 4;
    comp.attack.value = 0.004;
    comp.release.value = 0.25;
    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : this.volume;
    const ceiling = ctx.createWaveShaper();
    ceiling.curve = softCeiling();
    ceiling.oversample = '4x';
    const into = ctx.createGain();
    into.gain.value = 1 / CEILING_RANGE;
    comp.connect(this.master).connect(into).connect(ceiling).connect(ctx.destination);
    this.watch = ctx.createAnalyser();
    this.watch.fftSize = this.watchBuf.length;
    ceiling.connect(this.watch);
    this.sfx = ctx.createGain();
    this.sfx.connect(comp);
    const reverb = ctx.createConvolver();
    reverb.buffer = this.impulse(3.2, 2.4);
    this.reverbSend = ctx.createGain();
    this.reverbSend.gain.value = 0.5;
    this.reverbSend.connect(reverb).connect(comp);

    this.white = this.noise('white', 4);
    this.pink = this.noise('pink', 4);
    this.brown = this.noise('brown', 6);

    this.loops.sea = this.loop(this.brown, 'lowpass', 380, 0.7);
    this.loops.surf = this.loop(this.pink, 'bandpass', 520, 0.6);
    this.loops.hullP = this.loop(this.pink, 'bandpass', 900, 0.8, true);
    this.loops.hullS = this.loop(this.pink, 'bandpass', 900, 0.8, true);
    this.loops.wind = this.loop(this.pink, 'bandpass', 400, 0.5);
    this.loops.gustLow = this.loop(this.brown, 'lowpass', 160, 0.7);
    this.loops.rain = this.loop(this.white, 'highpass', 1400, 0.4);
    this.loops.deckRain = this.loop(this.white, 'bandpass', 2800, 0.9, true);
    // Fire roaring, water pouring into the hold.
    this.loops.fire = this.loop(this.pink, 'bandpass', 900, 0.5, true);
    this.loops.fireLow = this.loop(this.brown, 'lowpass', 260, 0.7, true);
    this.loops.ingress = this.loop(this.brown, 'lowpass', 700, 0.6, true);
    for (const [base, k, q] of [[460, 18, 30], [690, 24, 45], [1120, 31, 60], [1630, 36, 70]] as const) {
      const src = ctx.createBufferSource();
      src.buffer = this.white;
      src.loop = true;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = base;
      f.Q.value = q;
      const g = ctx.createGain();
      g.gain.value = 0;
      src.connect(f).connect(g).connect(this.sfx);
      src.start(0, Math.random() * 3);
      this.whistles.push({ f, g, base, k });
    }
  }

  private waking: (() => void) | null = null;

  setVolume(v: number): void {
    if (!Number.isFinite(v)) return;
    this.volume = Math.min(1, Math.max(0, v));
    if (this.ctx && this.master) this.master.gain.setTargetAtTime(this.muted ? 0 : this.volume, this.ctx.currentTime, 0.05);
  }

  toggleMute(): void {
    this.muted = !this.muted;
    this.setVolume(this.volume);
  }

  private noise(kind: 'white' | 'pink' | 'brown', seconds: number): AudioBuffer {
    const ctx = this.ctx!;
    const n = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(2, n, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0, last = 0;
      for (let i = 0; i < n; i++) {
        const w = Math.random() * 2 - 1;
        if (kind === 'white') d[i] = w * 0.5;
        else if (kind === 'pink') {
          b0 = 0.99886 * b0 + w * 0.0555179;
          b1 = 0.99332 * b1 + w * 0.0750759;
          b2 = 0.969 * b2 + w * 0.153852;
          b3 = 0.8665 * b3 + w * 0.3104856;
          b4 = 0.55 * b4 + w * 0.5329522;
          b5 = -0.7616 * b5 - w * 0.016898;
          d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
          b6 = w * 0.115926;
        } else {
          last = (last + 0.02 * w) / 1.02;
          d[i] = last * 3.2;
        }
      }
      // Crossfade the loop point.
      const fade = Math.floor(ctx.sampleRate * 0.05);
      for (let i = 0; i < fade; i++) {
        const t = i / fade;
        d[i] = d[i]! * t + d[n - fade + i]! * (1 - t);
      }
    }
    return buf;
  }

  private impulse(seconds: number, decay: number): AudioBuffer {
    const ctx = this.ctx!;
    const n = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(2, n, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / n, decay) * (i < 400 ? i / 400 : 1);
    }
    return buf;
  }

  /**
   * A panner for a sound at `p`. One-off sounds get the cheap equal-power
   * kind, put where they are at once (not swept in from the origin); only
   * the few long-running loops get HRTF, which costs ten times as much.
   */
  private panner(p?: V3, ref = 6, hrtf = false): PannerNode {
    const pn = this.ctx!.createPanner();
    pn.panningModel = hrtf ? 'HRTF' : 'equalpower';
    pn.distanceModel = 'inverse';
    pn.refDistance = ref;
    pn.rolloffFactor = 1;
    pn.maxDistance = 20000;
    if (p && finite3(p)) {
      pn.positionX.value = p.x;
      pn.positionY.value = p.y;
      pn.positionZ.value = p.z;
    }
    return pn;
  }

  /** Move a looping sound's panner (smoothly: it follows a moving source). */
  private place(pn: PannerNode, p: V3): void {
    if (!finite3(p)) return;
    const t = this.ctx!.currentTime;
    pn.positionX.setTargetAtTime(p.x, t, 0.05);
    pn.positionY.setTargetAtTime(p.y, t, 0.05);
    pn.positionZ.setTargetAtTime(p.z, t, 0.05);
  }

  /**
   * Room for one more one-off sound? Ones too far off to hear aren't made
   * at all; past the budget, ambience gives way first, then ordinary
   * sounds, so the great guns and the like are always heard. Counted until
   * `src` has played out.
   */
  private admit(prio: Prio, p: V3 | null, level: number, ref: number): boolean {
    if (!(level > 0) || !Number.isFinite(level)) return false;
    if (p) {
      if (!finite3(p)) return false;
      const d = Math.hypot(p.x - this.ear.x, p.y - this.ear.y, p.z - this.ear.z);
      if ((level * ref) / Math.max(ref, d) < 0.0015) return false;
    }
    const room = prio === 2 ? MAX_VOICES : prio === 1 ? MAX_VOICES * 0.75 : MAX_VOICES * 0.45;
    if (this.voices >= room) return false;
    this.voices++;
    return true;
  }

  private release(src: AudioScheduledSourceNode): void {
    src.onended = () => {
      this.voices = Math.max(0, this.voices - 1);
    };
  }

  private loop(buf: AudioBuffer, type: BiquadFilterType, freq: number, q: number, spatial = false): Loop {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    filter.Q.value = q;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    src.connect(filter).connect(gain);
    let panner: PannerNode | undefined;
    if (spatial) {
      panner = this.panner(undefined, 4, true);
      gain.connect(panner).connect(this.sfx);
    } else gain.connect(this.sfx);
    src.start(0, Math.random() * (buf.duration - 0.1));
    return { src, gain, filter, panner };
  }

  private set(p: AudioParam, v: number, tc = 0.12): void {
    if (Number.isFinite(v)) p.setTargetAtTime(v, this.ctx!.currentTime, tc);
  }

  /** One-shot filtered noise burst. */
  private burst(p: V3 | null, buf: AudioBuffer, type: BiquadFilterType, f0: number, f1: number, q: number, attack: number, dur: number, level: number, delay = 0, reverb = 0, ref = 6, prio: Prio = 1): void {
    if (!this.admit(prio, p, level, ref)) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime + Math.max(0, delay || 0);
    const src = ctx.createBufferSource();
    this.release(src);
    level = Math.min(level, 1.5);
    f0 = clampHz(f0, ctx);
    src.buffer = buf;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.Q.value = q;
    f.frequency.setValueAtTime(f0, t);
    f.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(level, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g);
    const out = p ? g.connect(this.panner(p, ref)) : g;
    out.connect(this.sfx);
    if (reverb > 0) {
      const s = ctx.createGain();
      s.gain.value = reverb;
      g.connect(s).connect(this.reverbSend);
    }
    src.start(t, Math.random() * (buf.duration - 0.1));
    src.stop(t + dur + 0.05);
  }

  private tone(p: V3 | null, type: OscillatorType, f0: number, f1: number, attack: number, dur: number, level: number, delay = 0, reverb = 0, filterHz = 0, prio: Prio = 1): void {
    if (!this.admit(prio, p, level, 6)) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime + Math.max(0, delay || 0);
    const o = ctx.createOscillator();
    this.release(o);
    level = Math.min(level, 1.5);
    f0 = clampHz(f0, ctx);
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(Math.max(10, f1), t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(level, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    let node: AudioNode = o;
    if (filterHz > 0) {
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = filterHz;
      node = node.connect(f);
    }
    node.connect(g);
    (p ? g.connect(this.panner(p)) : g).connect(this.sfx);
    if (reverb > 0) {
      const s = ctx.createGain();
      s.gain.value = reverb;
      g.connect(s).connect(this.reverbSend);
    }
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  /** Stick-slip creak of timber (or rope, higher). */
  private creak(p: V3, intensity: number, rope: boolean): void {
    const lvl = (rope ? 0.05 : 0.12) * Math.min(intensity, 2);
    if (!this.admit(rope ? 0 : 1, p, lvl, 3)) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const dur = rope ? 0.25 + Math.random() * 0.3 : 0.45 + Math.random() * 0.7;
    const o = ctx.createOscillator();
    this.release(o);
    o.type = 'sawtooth';
    const base = rope ? 140 + Math.random() * 120 : 38 + Math.random() * 50;
    o.frequency.setValueAtTime(base, t);
    // Irregular stick-slip pitch.
    for (let i = 1; i < 8; i++) o.frequency.linearRampToValueAtTime(base * (0.8 + Math.random() * 0.5), t + (dur * i) / 8);
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = rope ? 1400 + Math.random() * 900 : 380 + Math.random() * 500;
    f.Q.value = rope ? 9 : 5;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(lvl, t + dur * 0.2);
    g.gain.linearRampToValueAtTime(lvl * 0.6, t + dur * 0.6);
    g.gain.linearRampToValueAtTime(0.0001, t + dur);
    o.connect(f).connect(g).connect(this.panner(p, 3)).connect(this.sfx);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  private gull(p: V3): void {
    if (!this.admit(0, p, 0.09, 8)) return;
    const ctx = this.ctx!;
    const n = 2 + Math.floor(Math.random() * 4);
    let t = ctx.currentTime;
    const pn = this.panner(p, 8);
    pn.connect(this.sfx);
    for (let i = 0; i < n; i++) {
      const d = 0.16 + Math.random() * 0.12;
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      const f0 = 1300 + Math.random() * 300;
      o.frequency.setValueAtTime(f0, t);
      o.frequency.linearRampToValueAtTime(f0 * 1.7, t + d * 0.35);
      o.frequency.linearRampToValueAtTime(f0 * 1.15, t + d);
      const fm = ctx.createOscillator();
      fm.frequency.value = 160 + Math.random() * 60;
      const fmg = ctx.createGain();
      fmg.gain.value = 180;
      fm.connect(fmg).connect(o.frequency);
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = 2400;
      f.Q.value = 2.5;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.09, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + d);
      o.connect(f).connect(g).connect(pn);
      o.start(t);
      fm.start(t);
      o.stop(t + d + 0.02);
      fm.stop(t + d + 0.02);
      if (i === n - 1) this.release(o);
      t += d + 0.05 + Math.random() * 0.12;
    }
  }

  private bell(strikes: number): void {
    let t = 0;
    for (let i = 0; i < strikes; i++) {
      // Struck in pairs: "ding-ding ... ding-ding".
      t += i % 2 === 0 ? 0.9 : 0.32;
      for (const [ratio, amp, dec] of [[1, 0.12, 3.5], [2.41, 0.06, 2.2], [3.88, 0.04, 1.4], [5.4, 0.025, 1.0], [6.79, 0.015, 0.7]] as const) {
        this.tone(this.bellPos, 'sine', 690 * ratio, 690 * ratio * 0.998, 0.004, dec, amp, t, 0.05, 0, ratio < 3 ? 2 : 1);
      }
    }
  }

  private bellPos: V3 = { x: 0, y: 0, z: 0 };
  private crackleAcc = 0;
  private musketAcc = 0;
  private voiceTimer = 0;
  private cryTimer = 0;
  private bubbleAcc = 0;

  /** A man's voice: a buzzing glottal source through two vowel formants. */
  private voice(p: V3, f0: number, vowel: keyof typeof VOWELS, dur: number, level: number, delay = 0, glide = 0.85, prio: Prio = 0): void {
    if (!this.admit(prio, p, level, 5)) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime + Math.max(0, delay || 0);
    const o = ctx.createOscillator();
    this.release(o);
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(f0, t);
    o.frequency.linearRampToValueAtTime(f0 * 1.15, t + dur * 0.25);
    o.frequency.linearRampToValueAtTime(f0 * glide, t + dur);
    const vib = ctx.createOscillator();
    vib.frequency.value = 5 + Math.random() * 2;
    const vg = ctx.createGain();
    vg.gain.value = f0 * 0.02;
    vib.connect(vg).connect(o.frequency);
    const out = ctx.createGain();
    out.gain.setValueAtTime(0.0001, t);
    out.gain.exponentialRampToValueAtTime(level, t + 0.04);
    out.gain.setValueAtTime(level, t + dur * 0.7);
    out.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    const [f1, f2] = VOWELS[vowel]!;
    for (const [f, q, g] of [[f1, 6, 1], [f2, 9, 0.5]] as const) {
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = f * (0.95 + Math.random() * 0.1);
      bp.Q.value = q;
      const gg = ctx.createGain();
      gg.gain.value = g;
      o.connect(bp).connect(gg).connect(out);
    }
    const pn = this.panner(p, 5);
    out.connect(pn).connect(this.sfx);
    const s = ctx.createGain();
    s.gain.value = 0.25;
    out.connect(s).connect(this.reverbSend);
    o.start(t);
    vib.start(t);
    o.stop(t + dur + 0.05);
    vib.stop(t + dur + 0.05);
  }

  /** Many throats at once: a cheer or a roar across the water. */
  private crowd(p: V3, voices: number, level: number, delay = 0): void {
    for (let i = 0; i < voices; i++) {
      const f0 = 95 + Math.random() * 70;
      const d = delay + Math.random() * 0.35;
      this.voice({ x: p.x + (Math.random() - 0.5) * 8, y: p.y, z: p.z + (Math.random() - 0.5) * 8 }, f0, Math.random() < 0.5 ? 'a' : 'ae', 0.5 + Math.random() * 0.4, level / Math.sqrt(voices), d, 1.1);
      this.voice(p, f0, 'a', 0.9 + Math.random() * 0.5, level / Math.sqrt(voices), d + 0.55, 0.75);
    }
  }

  /** Oak splitting: a crack, a crunch and a thump. */
  /**
   * Our shot striking home, or theirs striking us: the sharp report of oak
   * stove in, carrying further than the rest of the din (so a hit across
   * the water is heard for what it is), over the ordinary crash.
   */
  private homeHit(p: V3, energy: number, delay: number, close: boolean, iron: boolean): void {
    const e = Math.min(1.5, 0.5 + energy);
    // The crack: very short, very bright.
    this.burst(p, this.white, 'highpass', 4200, 2600, 0.8, 0.0004, 0.045, 0.85 * e, delay, 0.25, close ? 14 : 45, 2);
    this.burst(p, this.white, 'bandpass', 1900, 900, 1.6, 0.0006, 0.09, 0.5 * e, delay + 0.004, 0.3, close ? 14 : 40, 2);
    // The body of it: a hollow knock.
    this.tone(p, 'triangle', iron ? 520 : 180, iron ? 470 : 90, 0.001, iron ? 0.5 : 0.16, 0.35 * e, delay, 0.3, 0, 2);
  }

  /** Whose ship is ours (for the hits we hear as ours). */
  private playerId = -1;

  private woodHit(p: V3, energy: number, delay: number, big = false): void {
    const e = Math.min(1.5, energy);
    this.burst(p, this.white, 'highpass', 3000, 1800, 0.7, 0.001, 0.07, 0.5 * e, delay, 0.2, 12);
    this.burst(p, this.brown, 'lowpass', big ? 1400 : 1000, 160, 0.8, 0.003, big ? 0.8 : 0.35, 0.8 * e, delay, 0.4, 12);
    this.tone(p, 'sine', big ? 70 : 95, 35, 0.003, big ? 0.6 : 0.25, 0.45 * e, delay, 0.3);
    // Splinters rattling down.
    for (let i = 0; i < 3; i++) this.burst(p, this.white, 'bandpass', 2400 + Math.random() * 2000, 1500, 3, 0.002, 0.05, 0.12 * e, delay + 0.08 + Math.random() * 0.4, 0, 8, 0);
  }

  update(dt: number, world: World, cam: { position: V3; forward: V3; up: V3 }, events: SimEvent[], shipToWorld: (x: number, y: number, z: number) => V3, onShip: boolean, gullPos: () => V3): void {
    if (!this.ctx) return;
    this.health(dt);
    const ctx = this.ctx;
    if (!ctx || ctx.state === 'closed') return;
    this.time += dt;
    this.splashes = 0;
    this.listen(cam);
    const ship = world.ship;
    const wind = world.wind;
    const p = world.weather.params;
    const hs = world.waves.significantHeight;
    const shipDist = Math.hypot(cam.position.x - ship.com.x, cam.position.y - ship.com.y, cam.position.z - ship.com.z);
    const near = Math.min(1, 60 / Math.max(10, shipDist));
    const lp = this.loops;

    // Open sea: a low roar that swells with the waves near the listener.
    const h = world.waves.heightAt(cam.position.x, cam.position.z, true);
    const swell = 0.6 + 0.4 * Math.sin(this.time * 0.7) * 0.5 + 0.2 * Math.min(1, Math.abs(h) / Math.max(hs, 0.3));
    this.set(lp.sea!.gain.gain, (0.1 + 0.16 * Math.min(1, hs / 5)) * swell);
    this.set(lp.surf!.gain.gain, (0.03 + 0.07 * Math.min(1, hs / 4)) * (0.7 + 0.3 * Math.sin(this.time * 1.3 + Math.sin(this.time * 0.37) * 3)));
    this.set(lp.surf!.filter.frequency, 420 + 300 * Math.min(1, wind.speed / 20));

    // Water rushing along the hull on each side.
    const speed = Math.abs(ship.speed);
    const rush = Math.min(1, speed / 6) * 0.7 + Math.min(1, Math.abs(ship.vel.y) / 2) * 0.4 + 0.05;
    for (const [key, side] of [['hullP', -1], ['hullS', 1]] as const) {
      const loop = lp[key]!;
      this.place(loop.panner!, shipToWorld(4, 0.2, side * 5.4));
      this.set(loop.gain.gain, rush * 0.22 * (1 + (side * ship.heel > 0 ? 0.4 : -0.2)));
      this.set(loop.filter.frequency, 500 + speed * 140);
    }

    // Wind over the sea and through the rigging.
    const ws = wind.speed;
    const aw = Math.hypot(ship.apparentWind.x, ship.apparentWind.z);
    const heard = onShip ? aw : ws;
    this.set(lp.wind!.gain.gain, Math.min(0.5, Math.pow(heard / 22, 2) * 0.55 + 0.01));
    this.set(lp.wind!.filter.frequency, 260 + heard * 26 + (wind.gust - 1) * 300);
    this.set(lp.gustLow!.gain.gain, Math.min(0.5, Math.pow(Math.max(0, heard - 6) / 18, 2) * 0.6 * wind.gust));
    for (const w of this.whistles) {
      const drift = Math.sin(this.time * (0.3 + w.k * 0.01) + w.base) * 0.04;
      this.set(w.f.frequency, w.base + heard * w.k * (1 + drift));
      this.set(w.g.gain, Math.max(0, (heard - 7) / 16) * 0.05 * near * (0.6 + 0.4 * Math.sin(this.time * 0.9 + w.base)), 0.3);
    }

    // Rain on the sea, and on deck when aboard.
    this.set(lp.rain!.gain.gain, p.rain * 0.18);
    this.set(lp.rain!.filter.frequency, 1100 + p.rain * 900);
    this.place(lp.deckRain!.panner!, shipToWorld(0, deckY(0) + 0.3, 0));
    this.set(lp.deckRain!.gain.gain, p.rain * 0.12 * (onShip ? 1 : near * 0.5));

    // Sails flogging when they lose the wind.
    let flutter = 0;
    for (const s of ship.sails.sails) flutter = Math.max(flutter, s.cloth.flutter * s.deploy);
    for (const f of ship.sails.flags) flutter = Math.max(flutter, f.cloth.flutter * 0.25);
    this.flapPhase += dt * (3 + aw * 0.35);
    if (this.flapPhase > 1) {
      this.flapPhase -= 1;
      const level = 0.02 + flutter * 0.2 * Math.min(1, aw / 8);
      if (level > 0.03 || Math.random() < 0.15) this.burst(shipToWorld(2, 22, 0), this.pink, 'lowpass', 1800, 500, 0.8, 0.004, 0.09, level * near, 0, 0, 10, 0);
    }

    // Gulls in fair weather, near the ship or land.
    this.gullTimer -= dt;
    if (this.gullTimer <= 0) {
      this.gullTimer = 3 + Math.random() * 9;
      if (p.rain < 0.3 && world.day.daylight > 0.3) this.gull(gullPos());
    }

    this.combat(dt, world, cam.position);
    this.playerId = world.player.id;
    for (const e of events) this.event(e, cam.position, shipToWorld);
  }

  /**
   * Put the listener at the camera, facing as it faces. Forward and up are
   * kept unit-length and at right angles; on a camera cut they're set at
   * once rather than eased (easing between opposite directions passes
   * through nothing at all, which a panner can't make sense of).
   */
  private listen(cam: { position: V3; forward: V3; up: V3 }): void {
    const L = this.ctx!.listener;
    if (!finite3(cam.position) || !finite3(cam.forward) || !finite3(cam.up)) return;
    const f = tmpF.set(cam.forward.x, cam.forward.y, cam.forward.z);
    if (f.length() < 1e-6) return;
    f.normalize();
    // Up, square to forward.
    const u = tmpU.set(cam.up.x, cam.up.y, cam.up.z);
    u.addScaled(f, -u.dot(f));
    if (u.length() < 1e-6) {
      // No usable up from the camera: the world's (or, looking straight up or down, east).
      u.set(Math.abs(f.y) < 0.9 ? 0 : 1, Math.abs(f.y) < 0.9 ? 1 : 0, 0);
      u.addScaled(f, -u.dot(f));
    }
    u.normalize();
    const p = cam.position;
    const cut = f.dot(this.lastFwd) < 0.7 || Math.hypot(p.x - this.ear.x, p.y - this.ear.y, p.z - this.ear.z) > 50;
    this.lastFwd.copy(f);
    this.ear.x = p.x;
    this.ear.y = p.y;
    this.ear.z = p.z;
    const t = this.ctx!.currentTime;
    const go = (a: AudioParam | undefined, v: number) => {
      if (!a) return;
      if (cut) {
        a.cancelScheduledValues(t);
        a.setValueAtTime(v, t);
      } else a.setTargetAtTime(v, t, 0.02);
    };
    if (L.positionX) {
      go(L.positionX, p.x);
      go(L.positionY, p.y);
      go(L.positionZ, p.z);
      go(L.forwardX, f.x);
      go(L.forwardY, f.y);
      go(L.forwardZ, f.z);
      go(L.upX, u.x);
      go(L.upY, u.y);
      go(L.upZ, u.z);
    } else {
      L.setPosition(p.x, p.y, p.z);
      L.setOrientation(f.x, f.y, f.z, u.x, u.y, u.z);
    }
  }

  /**
   * Once a second: wake a context the browser suspended, and if the output
   * has gone non-finite (a NaN anywhere stays in the filters and the
   * compressor and silences everything for good) build the sound afresh.
   */
  private health(dt: number): void {
    this.healthTimer -= dt;
    if (this.healthTimer > 0 || !this.ctx) return;
    this.healthTimer = 1;
    const ctx = this.ctx;
    if (ctx.state === 'suspended' && document.visibilityState === 'visible') void ctx.resume();
    if (ctx.state !== 'running') return;
    this.watch.getFloatTimeDomainData(this.watchBuf);
    for (const v of this.watchBuf) {
      if (Number.isFinite(v)) continue;
      this.ctx = null;
      this.loops = {};
      this.whistles = [];
      void ctx.close();
      this.start();
      return;
    }
  }

  /** Continuous sounds of the fight: fire, water coming in, musketry, voices. */
  private combat(dt: number, world: World, listener: V3): void {
    const lp = this.loops;
    const dist = (p: V3) => Math.hypot(p.x - listener.x, p.y - listener.y, p.z - listener.z);
    // The nearest, fiercest fire.
    let best = 0;
    let bp: V3 | null = null;
    for (const v of world.vessels) {
      if (v.fire.burning === 0) continue;
      for (const c of v.fire.cells) {
        if (c.heat < 0.05) continue;
        const p = v.ship.designToWorld(tmpA.set(c.x, c.y, c.z), tmpA);
        const lvl = c.heat / (1 + dist(p) / 25);
        if (lvl > best) {
          best = lvl;
          bp = { x: p.x, y: p.y, z: p.z };
        }
      }
    }
    if (bp) {
      this.place(lp.fire!.panner!, bp);
      this.place(lp.fireLow!.panner!, bp);
    }
    this.set(lp.fire!.gain.gain, Math.min(0.5, best * 0.6), 0.3);
    this.set(lp.fireLow!.gain.gain, Math.min(0.6, best * 0.8), 0.3);
    this.crackleAcc += dt * best * 30;
    while (this.crackleAcc > 1 && bp) {
      this.crackleAcc -= 1;
      this.burst(bp, this.white, 'bandpass', 1500 + Math.random() * 3000, 900, 2, 0.001, 0.03 + Math.random() * 0.05, 0.25 * Math.min(1, best * 2), Math.random() * 0.1, 0, 6, 0);
    }

    // Water pouring in through holes on our own ship.
    const me = world.player;
    const leak = me.damage.leakArea;
    this.place(lp.ingress!.panner!, me.ship.designToWorld(tmpA.set(0, -0.5, 0), tmpA));
    this.set(lp.ingress!.gain.gain, Math.min(0.4, leak * 1.5) * (dist(me.ship.com) < 60 ? 1 : 0.2), 0.4);
    this.set(lp.ingress!.filter.frequency, 400 + Math.min(1, leak * 3) * 700, 0.4);

    // Muskets popping from the tops and rails; voices; the drowning.
    let marines = 0;
    let mp: V3 | null = null;
    this.voiceTimer -= dt;
    this.cryTimer -= dt;
    for (const v of world.vessels) {
      if (!v.battle || !v.fighting) continue;
      const d = dist(v.ship.com);
      if (d > 700) continue;
      for (const m of v.crew.members) {
        if (m.alive && m.stance === 'aim') {
          marines++;
          if (!mp || Math.random() < 0.2) mp = v.ship.designToWorld(tmpA.set(m.x, deckY(m.x) + 1.4, m.z), new Vec3());
        }
      }
      // An officer bawling orders now and then.
      if (this.voiceTimer <= 0 && d < 250 && Math.random() < dt * 2) {
        this.voiceTimer = 1.5 + Math.random() * 3;
        const o = v.crew.members.find((m) => m.alive && m.role === 'officer') ?? v.crew.members.find((m) => m.alive);
        if (o) {
          const p = v.ship.designToWorld(tmpA.set(o.x, deckY(o.x) + 1.6, o.z), new Vec3());
          const words: (keyof typeof VOWELS)[][] = [['a', 'o'], ['ae', 'a'], ['o', 'e', 'a'], ['e', 'a']];
          const w = words[Math.floor(Math.random() * words.length)]!;
          w.forEach((vw, i) => this.voice(p, 120 + Math.random() * 30, vw, 0.22 + Math.random() * 0.15, 0.22, i * 0.26));
        }
      }
      if (v.state === 'sinking' && d < 600) {
        this.bubbleAcc += dt * 6;
        while (this.bubbleAcc > 1) {
          this.bubbleAcc -= 1;
          const p = { x: v.ship.com.x + (Math.random() - 0.5) * 20, y: v.ship.com.y, z: v.ship.com.z + (Math.random() - 0.5) * 8 };
          this.tone(p, 'sine', 140 + Math.random() * 200, 400 + Math.random() * 300, 0.01, 0.12, 0.12, Math.random() * 0.2, 0, 0, 0);
        }
      }
    }
    this.musketAcc += dt * marines * 0.25;
    while (this.musketAcc > 1 && mp) {
      this.musketAcc -= 1;
      const d = dist(mp) / SPEED_OF_SOUND;
      this.burst(mp, this.white, 'bandpass', 1800, 700, 0.9, 0.001, 0.14, 0.4, d + Math.random() * 0.3, 0.6, 12, 0);
      this.tone(mp, 'sine', 180, 60, 0.002, 0.1, 0.15, d, 0, 0, 0);
    }
  }

  private event(e: SimEvent, listener: V3, shipToWorld: (x: number, y: number, z: number) => V3): void {
    const dist = (p: V3) => Math.hypot(p.x - listener.x, p.y - listener.y, p.z - listener.z);
    switch (e.type) {
      case 'creak': {
        this.creak(e, e.intensity, false);
        if (Math.random() < 0.5) this.creak(shipToWorld(1 + Math.random() * 3, 12 + Math.random() * 14, (Math.random() - 0.5) * 6), e.intensity * 0.8, true);
        break;
      }
      case 'slam': {
        const d = dist(e) / SPEED_OF_SOUND;
        this.tone(e, 'sine', 70, 32, 0.01, 0.9, 0.5 * e.intensity, d, 0, 0, 2);
        this.burst(e, this.brown, 'lowpass', 500, 120, 0.7, 0.01, 1.0, 0.7 * e.intensity, d, 0.1, 6, 2);
        this.burst(e, this.white, 'bandpass', 2600, 700, 0.6, 0.02, 1.6, 0.25 * e.intensity, d + 0.05, 0, 6, 1);
        break;
      }
      case 'washOver':
        this.burst(e, this.pink, 'bandpass', 1800, 450, 0.5, 0.05, 2.2, 0.4 * e.intensity, 0, 0, 6, 2);
        this.burst(e, this.brown, 'lowpass', 400, 150, 0.7, 0.05, 1.5, 0.35 * e.intensity, 0.05, 0, 6, 2);
        break;
      case 'splash': {
        if (++this.splashes > SPLASHES_PER_FRAME) break;
        const d = dist(e) / SPEED_OF_SOUND;
        this.burst(e, this.white, 'bandpass', 1500 * e.size, 400, 0.7, 0.01, 1.1, 0.6 * e.size, d, 0, 12);
        this.tone(e, 'sine', 120, 60, 0.005, 0.3, 0.2 * e.size, d);
        break;
      }
      case 'cannonFire': {
        const dd = dist(e);
        const d = dd / SPEED_OF_SOUND;
        const far = Math.min(1, dd / 400);
        // Blast: a shaped noise burst with a sub-bass thump and a long tail.
        this.burst(e, this.white, 'lowpass', 5000 * (1 - far * 0.8), 180, 0.7, 0.002, 1.8, 1.0, d, 0.9, 15, 2);
        this.tone(e, 'sine', 95, 32, 0.004, 1.2, 0.9, d, 0.5, 0, 2);
        this.burst(e, this.brown, 'lowpass', 600, 60, 0.8, 0.02, 3.5, 0.6, d + 0.05, 1.2, 15, 1);
        // Echo from the sea surface and distant shore.
        this.burst(e, this.brown, 'lowpass', 300, 80, 0.8, 0.2, 2.5, 0.12, d + 1.2 + Math.random() * 0.8, 0.8, 40, 0);
        break;
      }
      case 'lightning': {
        const d = e.distance / SPEED_OF_SOUND;
        const close = Math.max(0, 1 - e.distance / 3000);
        const lvl = Math.min(1, 1600 / e.distance) * e.intensity;
        const cut = 180 + close * 1400;
        if (close > 0.2) this.burst(null, this.white, 'lowpass', 6000, 800, 0.5, 0.002, 0.5, 0.9 * close, d, 0.8, 6, 2);
        for (let i = 0; i < 5; i++) {
          this.burst(null, this.brown, 'lowpass', cut, 60, 0.6, 0.15 + Math.random() * 0.4, 3 + Math.random() * 5, 0.5 * lvl * (1 - i * 0.12), d + i * (0.3 + Math.random() * 0.9), 1.0, 6, i < 2 ? 2 : 1);
        }
        break;
      }
      case 'impact': {
        const d = dist(e) / SPEED_OF_SOUND;
        if (dist(e) > 3000) break;
        // Ours home on her, or hers on us: heard sharp and clear.
        const home = e.by === this.playerId && e.vessel !== this.playerId;
        const onUs = e.vessel === this.playerId && e.by !== undefined && e.by >= 0;
        if ((home || onUs) && e.what !== 'sail' && e.what !== 'rigging') this.homeHit(e, e.energy, d, onUs, e.what === 'gun');
        if (e.what === 'sail') this.burst(e, this.pink, 'bandpass', 1600, 3000, 1.2, 0.002, 0.25, 0.3 * Math.min(1, e.energy), d, 0.1, 10);
        else if (e.what === 'rigging') this.burst(e, this.white, 'bandpass', 900, 2600, 3, 0.001, 0.12, 0.18, d, 0.1, 8);
        else if (e.what === 'gun') {
          // Iron on iron: an ugly clang.
          for (const [f, a] of [[410, 0.25], [1030, 0.12], [1790, 0.08], [2610, 0.05]] as const) this.tone(e, 'triangle', f, f * 0.97, 0.001, 0.9, a, d, 0.2);
          this.woodHit(e, e.energy * 0.6, d);
        } else this.woodHit(e, e.energy, d, e.what === 'mast');
        break;
      }
      case 'explosion': {
        const dd = dist(e);
        const d = dd / SPEED_OF_SOUND;
        const k = Math.sqrt(e.size);
        const far = Math.min(1, dd / 1500);
        this.burst(e, this.white, 'lowpass', 7000 * (1 - far * 0.85), 150, 0.6, 0.002, 1.2 + k, Math.min(1, 0.6 * k), d, 1.2, 25, 2);
        this.tone(e, 'sine', 70, 22, 0.004, 1.5 + k * 0.8, Math.min(1, 0.7 * k), d, 0.8, 0, 2);
        this.burst(e, this.brown, 'lowpass', 900, 40, 0.8, 0.02, 3 + k * 1.5, Math.min(1, 0.7 * k), d + 0.05, 1.5, 25, 2);
        // Debris raining down and splashing.
        for (let i = 0; i < 4 * k; i++) this.burst(e, this.white, 'bandpass', 2000 + Math.random() * 1500, 600, 1.5, 0.005, 0.25, 0.1, d + 0.8 + Math.random() * 2.5, 0.1, 12, 0);
        break;
      }
      case 'collapse': {
        const d = dist(e) / SPEED_OF_SOUND;
        this.creak(e, 1.2, false);
        this.woodHit(e, 1.2 * e.size, d + 0.4, true);
        break;
      }
      case 'sailTorn': {
        // Canvas splitting: a long rip, and the ends flogging.
        if (dist(e) > 2500) break;
        const d = dist(e) / SPEED_OF_SOUND;
        const lvl = e.level === 2 ? 0.55 : 0.3;
        this.burst(e, this.pink, 'bandpass', 900, 3200, 1.4, 0.01, e.level === 2 ? 0.7 : 0.35, lvl, d, 0.2, 30, 1);
        for (let i = 0; i < (e.level === 2 ? 6 : 2); i++) this.burst(e, this.white, 'bandpass', 1500 + Math.random() * 1500, 700, 2.5, 0.002, 0.06, lvl * 0.5, d + 0.15 + i * (0.08 + Math.random() * 0.1), 0.1, 25, 0);
        break;
      }
      case 'mastFall': {
        const d = dist(e) / SPEED_OF_SOUND;
        // The crack, the long groan as she goes, and the crash alongside.
        this.woodHit(e, 1.5, d, true);
        this.creak(e, 1.4, false);
        this.creak(e, 1.2, true);
        for (let i = 0; i < 5; i++) this.burst(e, this.white, 'bandpass', 900 + Math.random() * 1500, 2000, 4, 0.002, 0.1, 0.15, d + 0.3 + i * 0.25, 0.1, 10, 0);
        this.burst(e, this.brown, 'lowpass', 700, 90, 0.7, 0.05, 2.2, 0.8, d + 2.2, 0.8, 15, 2);
        this.burst(e, this.white, 'bandpass', 1400, 400, 0.6, 0.02, 1.8, 0.5, d + 2.3, 0.3, 15, 2);
        break;
      }
      case 'collision': {
        const d = dist(e) / SPEED_OF_SOUND;
        this.woodHit(e, 1.5 * e.energy + 0.5, d, true);
        this.creak(e, 1.5 * e.energy + 0.4, false);
        this.creak(e, 1.2 * e.energy + 0.3, false);
        break;
      }
      case 'signal': {
        const d = dist(e) / SPEED_OF_SOUND;
        this.burst(e, this.white, 'lowpass', 2500, 150, 0.7, 0.002, 1.4, 0.6, d, 0.9, 15, 2);
        this.tone(e, 'sine', 85, 35, 0.004, 0.9, 0.5, d, 0.4, 0, 2);
        break;
      }
      case 'boarding': {
        const p = { x: e.x, y: 3, z: e.z };
        this.crowd(p, 10, 0.5, dist(p) / SPEED_OF_SOUND);
        break;
      }
      case 'casualty': {
        if (this.cryTimer > 0 || dist(e) > 80) break;
        this.cryTimer = 2 + Math.random() * 3;
        this.voice(e, 190 + Math.random() * 60, Math.random() < 0.5 ? 'a' : 'o', 0.5 + Math.random() * 0.4, 0.12, dist(e) / SPEED_OF_SOUND, 0.6, 1);
        break;
      }
      case 'objectHit':
        this.tone(e, 'sine', 110, 60, 0.002, 0.18, 0.25 * e.intensity);
        this.burst(e, this.brown, 'lowpass', 700, 200, 0.7, 0.002, 0.2, 0.3 * e.intensity);
        break;
      case 'bell':
        this.bellPos = shipToWorld(BELFRY.x + 0.3, deckY(12.8) + 1.1, 0);
        this.bell(e.strikes);
        break;
      default:
        break;
    }
  }
}

/** The soft ceiling's input range (the shaper sees x / CEILING_RANGE). */
const CEILING_RANGE = 4;

/**
 * Unity up to 0.6 of full scale, then bending smoothly toward it: ordinary
 * levels pass untouched, a pile-up of loud sounds can't clip or blast.
 */
function softCeiling(): Float32Array<ArrayBuffer> {
  const n = 4096, knee = 0.6, out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = ((i / (n - 1)) * 2 - 1) * CEILING_RANGE;
    const a = Math.abs(x);
    out[i] = Math.sign(x) * (a <= knee ? a : knee + (1 - knee) * Math.tanh((a - knee) / (1 - knee)));
  }
  return out;
}

function finite3(p: V3): boolean {
  return Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);
}

/** A frequency a filter or oscillator can take here (positive, below Nyquist). */
function clampHz(f: number, ctx: BaseAudioContext): number {
  return Number.isFinite(f) ? Math.min(Math.max(f, 10), ctx.sampleRate * 0.45) : 440;
}

const tmpA = new Vec3();
const tmpF = new Vec3();
const tmpU = new Vec3();
